import { TERMS_VERSION } from "@instead/shared";
import { api } from "./api.ts";

/**
 * Terms and privacy notice acceptance (D-38). The sign-in screen says "by
 * continuing you accept…"; continuing marks the version on the device, and
 * the first gated load records it on the server (the consents log). Someone
 * already signed in when a version bumps never saw that line, so they get
 * one calm banner with a Continue — recorded the same way, once.
 *
 * `instead.terms` holds either the version the person accepted on this device
 * ("<version>") or the version the server has on record ("<version>:recorded").
 * wipeDevice() clears it with everything else.
 */
const KEY = "instead.terms";
const RECORDED = `${TERMS_VERSION}:recorded`;

let pending = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function read(): string | null {
  try { return localStorage.getItem(KEY); } catch { return null; }
}
function write(v: string): void {
  try { localStorage.setItem(KEY, v); } catch { /* storage unavailable — the server row is what counts */ }
}

export const terms = {
  version: TERMS_VERSION,
  /** The person continued past the sign-in line: remember it until the server has it. */
  markAccepted(): void { write(TERMS_VERSION); },
  /** The banner asks once; Continue records the acceptance. */
  async accept(): Promise<void> {
    await api.account.acceptTerms(TERMS_VERSION);
    write(RECORDED);
    pending = false;
    notify();
  },
  subscribe(l: () => void): () => void { listeners.add(l); return () => listeners.delete(l); },
  isPending: (): boolean => pending,
};

/** After the gate passed: make sure the current version is on record, or ask. */
export async function ensureTermsRecorded(): Promise<void> {
  const local = read();
  if (local === RECORDED) return;
  const { consents } = await api.account.consents();
  const onServer = consents.some((c) => c.kind === "terms" && c.version === TERMS_VERSION && !c.revokedAt);
  if (onServer) { write(RECORDED); return; }
  if (local === TERMS_VERSION) { await terms.accept(); return; } // accepted on the sign-in screen, not yet recorded
  pending = true; // signed in before this version: ask once
  notify();
}
